'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import pinShowroom from 'assets/images/pin-showroom.png';
import pinPartnerStudio from 'assets/images/pin-partner-studio.png';
import { buildDirectionsUrl, buildDetailUrl, STORE_TYPE_PARTNER } from './helpers';
import { setOptions, importLibrary } from './loadGoogleMaps';

// Hardcoded — same pattern as ModularItem.js. TODO: move to infra-injected env.
const GOOGLE_MAPS_API_KEY = 'AIzaSyDAJPjW4VROH-7VcPMsvw5-8K3DtiA3mSw';

// Owns the map's visual style; theme can be edited in GCP Maps Studio without a code change.
const GOOGLE_MAPS_MAP_ID = '29a117773f6329616ec6e6a0';
const DEFAULT_CENTER = {
  lat: 39.8283,
  lng: -98.5795
};
const DEFAULT_ZOOM = 4;
const buildInfoWindowHtml = store => {
  const detailUrl = buildDetailUrl(store);
  const directionsUrl = buildDirectionsUrl(store);
  const title = store.name || `${store.city || ''}, ${store.state || ''}`;
  const addressLines = [store.street, [store.city, store.state, store.zip].filter(Boolean).join(', ')].filter(Boolean).map(line => `<div>${line}</div>`).join('');
  const hoursBlock = store.open_hours ? `<div class="mt-2 whitespace-pre-line">${store.open_hours}</div>` : '';
  const phoneBlock = store.phone ? `<div>${store.phone}</div>` : '';
  return `
    <div class="max-w-60">
      <div class="font-bold text-base mb-1">${title}</div>
      <div class="text-sm text-gray-light1">${addressLines}${phoneBlock}${hoursBlock}</div>
      <div class="mt-2.5 flex gap-2">
        <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" class="text-gray-dark underline font-bold">Directions</a>
        <a href="${detailUrl}" class="text-gray-dark underline font-bold">Details</a>
      </div>
    </div>
  `;
};

// Next.js resolves image imports to { src, ... }; webpack resolves them to a string.
const getAssetUrl = asset => typeof asset === 'string' ? asset : asset && asset.src;
const buildMarkerContent = isPartner => {
  const img = document.createElement('img');
  img.src = getAssetUrl(isPartner ? pinPartnerStudio : pinShowroom);
  img.width = 32;
  img.height = 40;
  img.style.transform = 'translateY(-20px)';
  img.alt = '';
  return img;
};
const StoreLocatorMap = t0 => {
  const $ = _c(15);
  const {
    stores,
    activeStoreId,
    onMarkerClick
  } = t0;
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const markersRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const markerStoreMapRef = useRef(t2);
  const infoWindowRef = useRef(null);
  const mapsLibRef = useRef(null);
  const coreLibRef = useRef(null);
  const markerLibRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let cancelled = false;
      setOptions({
        key: GOOGLE_MAPS_API_KEY,
        v: "quarterly"
      });
      Promise.all([importLibrary("maps"), importLibrary("core"), importLibrary("marker")]).then(t5 => {
        const [mapsLib, coreLib, markerLib] = t5;
        if (cancelled || !containerRef.current) {
          return;
        }
        mapsLibRef.current = mapsLib;
        coreLibRef.current = coreLib;
        markerLibRef.current = markerLib;
        mapRef.current = new mapsLib.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: GOOGLE_MAPS_MAP_ID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
        infoWindowRef.current = new mapsLib.InfoWindow();
        setIsReady(true);
      }).catch(err => {
        if (!cancelled) {
          setLoadError(err);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== isReady || $[5] !== onMarkerClick || $[6] !== stores) {
    t5 = () => {
      if (!isReady || !mapRef.current) {
        return;
      }
      const coreLib_0 = coreLibRef.current;
      const markerLib_0 = markerLibRef.current;
      if (!coreLib_0 || !markerLib_0) {
        return;
      }
      markersRef.current.forEach(_temp);
      markersRef.current = [];
      markerStoreMapRef.current = new Map();
      const validStores = (stores || []).filter(_temp2);
      if (validStores.length === 0) {
        return;
      }
      const bounds = new coreLib_0.LatLngBounds();
      validStores.forEach(store => {
        const position = {
          lat: store.latitude,
          lng: store.longitude
        };
        const marker = new markerLib_0.AdvancedMarkerElement({
          position,
          map: mapRef.current,
          title: store.name || "",
          content: buildMarkerContent(store.type === STORE_TYPE_PARTNER)
        });
        marker.addListener("gmp-click", () => {
          infoWindowRef.current.setContent(buildInfoWindowHtml(store));
          infoWindowRef.current.open({
            anchor: marker,
            map: mapRef.current
          });
          if (onMarkerClick) {
            onMarkerClick(store.id);
          }
        });
        markersRef.current.push(marker);
        markerStoreMapRef.current.set(store.id, {
          marker,
          store
        });
        bounds.extend(position);
      });
      if (validStores.length > 1) {
        mapRef.current.fitBounds(bounds);
      } else {
        mapRef.current.setCenter(bounds.getCenter());
        mapRef.current.setZoom(10);
      }
    };
    t6 = [stores, isReady, onMarkerClick];
    $[4] = isReady;
    $[5] = onMarkerClick;
    $[6] = stores;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== activeStoreId || $[10] !== isReady) {
    t7 = () => {
      if (!isReady || activeStoreId == null) {
        return;
      }
      const entry = markerStoreMapRef.current.get(activeStoreId);
      if (!entry) {
        return;
      }
      const {
        marker: marker_0,
        store: store_0
      } = entry;
      infoWindowRef.current.setContent(buildInfoWindowHtml(store_0));
      infoWindowRef.current.open({
        anchor: marker_0,
        map: mapRef.current
      });
      if (marker_0.position) {
        mapRef.current.panTo(marker_0.position);
      }
    };
    t8 = [activeStoreId, isReady];
    $[9] = activeStoreId;
    $[10] = isReady;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  if (loadError) {
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-gray-light7 text-gray-light1 text-center p-4">Map unavailable</div>;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div ref={containerRef} className="w-full h-full min-h-[400px] bg-gray-light7" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
StoreLocatorMap.propTypes = {
  stores: PropTypes.array.isRequired,
  activeStoreId: PropTypes.number,
  onMarkerClick: PropTypes.func
};
export default StoreLocatorMap;
function _temp(m) {
  m.map = null;
}
function _temp2(s) {
  return typeof s.latitude === "number" && typeof s.longitude === "number";
}